import React from 'react';
import { pxToRem } from '@withjoy/joykit/theme';
import { Box, Flex, TextV2, CloseButton, Divider, LinkV2 } from '@withjoy/joykit';
import { ResponsiveDialog } from '@shared/components/ResponsiveDialog/ResponsiveDialog';
import { closeButtonOverrides, drawerOverrides } from '@shared/components/ResponsiveDialog/ResponsiveDialog.styles';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { hed3ToSubHeadingOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useScaleGuestSiteFontSize } from '@apps/guest/components/GuestSiteTypographyOverride/hooks/useScaleGuestSiteFontSize';
import { StarRating } from '@shared/components';
import { VenueAndTravelDistance } from '../../../VenueAndTravelDistance';
import { AccommodationCombined } from '../../../../../Accommodations/Accommodation.types';
import { DEFAULT_HOTEL_IMAGE } from '@shared/utils/constants';
import { Category } from '@graphql/generated';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { JoyPlacePricingV2 } from '../../../JoyPlacePricingV2';
import { hasAccommodationLink } from '../../../../../Accommodations/Accommodation.utils';
import { CTAButton } from '../../../CTAButton';
import { useAccommodationsCta } from '../../../../../../hooks/useAccommodationsCta';
import { EventVenue } from '@shared/utils/eventPlace';
import { ComparisonIcon, Provider, telemetryDestination } from '../../CompareToPrice';
import { useTranslation } from '@shared/core';
import { OpenInNewWindow } from '@withjoy/joykit/icons';
import { useRemoteFeatureFlag } from '@shared/core/featureFlags/remoteFeatureFlags';
import { wrapPartnerHotelRedirect } from '../../../../../WrapPartnerHotelRedirect';
import { useCustomPageTelemetry } from '@apps/guest/packages/layout-engine/widgets/Custom/Custom.telemetry';
import { Rate } from '@shared/hooks/useTravelPlacesPriceComparison/useTravelPlacesPriceComparison';
import { createSearchParamsFromAccommodationsPage } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';

type ComparisonDialogProps = Readonly<{
  eventHandle: string;
  isOpen: boolean;
  onClose: () => void;
  accommodation: AccommodationCombined;
  locationInfo?: EventVenue;
  rates: Rate[];
  onSelectAccommodation?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}>;

const TELEMETRY_SOURCE = 'priceCompareDialog';

export const ComparisonDialog: React.FC<ComparisonDialogProps> = wrapPartnerHotelRedirect(({ isOpen, onClose, accommodation, locationInfo, rates, onSelectAccommodation }) => {
  const { value, loading } = useRemoteFeatureFlag('accommodationsPriceComparisonExperiment');
  const { t: accommodationsTrans } = useTranslation('guestSiteCustom');
  const accTrans = accommodationsTrans('accommodations');
  const { ctaTitle, ctaUrl } = useAccommodationsCta({
    accommodation,
    locationInfo,
    searchParams: createSearchParamsFromAccommodationsPage('price_compare_dialog'),
  });
  const [fontPackExperimentEnabled] = useIsFeatureEnabled('fontPackExperiment');
  const { scaleFontSize } = useScaleGuestSiteFontSize();

  const hasLink = hasAccommodationLink(accommodation.isCustomLinkEnabled, accommodation.customUrl);
  const { photo, displayName, distance, distances, starRating } = accommodation;

  const telemetry = useCustomPageTelemetry();

  if (!rates.length || loading || value === 'no-comparison') {
    return null;
  }

  const telemetryHotelInfo = {
    hotelName: displayName || '',
    isHotelPreferred: accommodation.isFavorite || false,
    hotelPrice: accommodation.pricePerNight || undefined,
    hotelStrikeThroughPrice: accommodation.strikeoutPricePerNight || undefined,
    type: accommodation.type,
  };

  const handleOnCTAButtonClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    e.preventDefault();
    onSelectAccommodation(e);
  };

  const handleOnLinkClick = (url: string, provider: string) =>
    telemetry.accommodationsLinkInteracted(url, { ...telemetryHotelInfo, destination: telemetryDestination[provider as Provider] }, TELEMETRY_SOURCE);

  return (
    <ResponsiveDialog
      isOpen={isOpen}
      onClose={onClose}
      hideDivider
      removeBodyPadding
      hasCloseButton={false}
      drawerOverrides={{
        ...drawerOverrides,
        Content: {
          props: {
            ...drawerOverrides.Content?.props,
            boxShadow: '0px -7px 16px -8px rgba(0, 0, 0, 0.3), 0px -10px 27px -5px rgba(44, 41, 37, 0.25)',
          },
        },
        Body: { props: { ...drawerOverrides.Body?.props, paddingY: 0, paddingX: 0 } },
      }}
      dialogOverrides={{
        Content: { props: { width: '100%', maxWidth: 620 } },
        Body: { props: { paddingX: 0, paddingY: 0 } },
      }}
    >
      <Box
        position="relative"
        height="100%"
        width="100%"
      >
        <CloseButton
          overrides={{
            Root: {
              props: {
                ...closeButtonOverrides.Root?.props,
                position: 'absolute',
                top: pxToRem(16),
                right: pxToRem(16),
                zIndex: 1,
                width: pxToRem(32),
                height: pxToRem(32),
                minWidth: pxToRem(32),
                background: 'rgba(255, 255, 255, 0.20)',
                backdropFilter: 'blur(8px)',
                border: 'none',
              },
            },
            Icon: { props: { ...closeButtonOverrides.Icon?.props } },
          }}
          onClick={onClose}
          aria-label={'close dialog'}
        />
        <Flex
          flexDirection="column"
          gap={6}
        >
          <Box
            width="100%"
            height={pxToRem(206)}
            borderTopLeftRadius={{ _: 0, sm2: pxToRem(12) }}
            borderTopRightRadius={{ _: 0, sm2: pxToRem(12) }}
            objectFit="cover"
            backgroundSize="cover"
            backgroundPosition="center bottom"
            backgroundRepeat="no-repeat"
            backgroundImage={`url(${photo?.url || DEFAULT_HOTEL_IMAGE})`}
            display="flex"
            position="relative"
          />
          <Flex
            flexDirection="column"
            gap={7}
            alignItems="center"
          >
            <Flex
              width="100%"
              flexDirection="column"
              gridTemplateColumns="1fr 2fr"
              gap={6}
              paddingX={{ _: 5, sm2: pxToRem(24) }}
              paddingBottom={pxToRem(24)}
            >
              <Flex
                flexDirection="column"
                gap={6}
              >
                <Flex
                  flexDirection="column"
                  gap={3}
                  alignItems="flex-start"
                >
                  <GuestSiteTypographyOverride
                    override={hed3ToSubHeadingOverride}
                    defaultFontFamily="Inter UI"
                  >
                    <TextV2
                      typographyVariant="hed5"
                      {...(!fontPackExperimentEnabled && { fontFamily: 'Inter UI' })}
                      fontSize={scaleFontSize(pxToRem(20), Category.SUB_HEADING)}
                      fontWeight={600}
                    >
                      {displayName}
                    </TextV2>
                  </GuestSiteTypographyOverride>
                  <Flex
                    gap={4}
                    alignItems="center"
                    justifyContent="flex-start"
                  >
                    {!!starRating && (
                      <StarRating
                        rating={starRating}
                        id={accommodation.id}
                        hideNonFilledStars
                      />
                    )}
                    <VenueAndTravelDistance
                      milesToVenue={distance}
                      distances={distances}
                    />
                  </Flex>
                </Flex>
                <JoyPlacePricingV2
                  accommodation={accommodation}
                  hideComparison
                />
                {hasLink && (
                  <CTAButton
                    ctaTitle={ctaTitle}
                    width="100%"
                    variant="solid"
                    typographyVariant="button2"
                    {...{
                      as: 'a',
                      href: ctaUrl,
                      onClick: handleOnCTAButtonClick,
                      target: '_blank',
                    }}
                  />
                )}
                <Flex
                  flexDirection="column"
                  gap={4}
                  alignItems="flex-start"
                  justifyContent="center"
                  marginTop={4}
                >
                  <TextV2 typographyVariant="hed1">{accTrans.comparedTo()}</TextV2>
                  {rates.map(({ provider, price, url }) => (
                    <React.Fragment key={provider}>
                      <Divider />
                      <Flex
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                      >
                        {ComparisonIcon[provider]?.large ?? null}
                        <Flex
                          alignItems="center"
                          justifyContent="center"
                          gap={1}
                        >
                          <TextV2>{price}</TextV2>
                          {value === 'link' && (
                            <LinkV2
                              href={url}
                              _activeLink={{ color: 'mono14' }}
                              _visited={{ color: 'mono14' }}
                              _active={{ color: 'mono14' }}
                              _pressed={{ color: 'mono14' }}
                              color="mono14"
                              textDecoration="none"
                              _hover={{ textDecoration: 'underline' }}
                              target="_blank"
                              fontSize="1px"
                              onClick={() => handleOnLinkClick(url, provider)}
                            >
                              <OpenInNewWindow />
                            </LinkV2>
                          )}
                        </Flex>
                      </Flex>
                    </React.Fragment>
                  ))}
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Box>
    </ResponsiveDialog>
  );
}, TELEMETRY_SOURCE);
